/**
 * Anonymous birth-date sessionStorage helpers (HORO-10330).
 *
 * Pure helpers — no React, no Redux — so non-component code (useAuth)
 * can import them without dragging authSlice into the import graph
 * (avoids the circular dependency that bit us with daily tarot
 * credits; see [[feedback-bash-cwd-resets]] for the post-mortem
 * pattern).
 *
 * Storage choice: sessionStorage, not localStorage. Per HORO-10330's
 * legal-posture decision, the anonymous birth date should clear on
 * tab close. Users get a single-input experience within a session but
 * the value doesn't persist across browser sessions.
 *
 * Migration semantics live in `useAuth.js`:
 *   - profile.birthDate empty + anon value present → POST anon value,
 *     then clear local.
 *   - profile.birthDate already set → discard local (no overwrite).
 */
import { STORAGE_KEY_BIRTH_DATE, STORAGE_KEY_PENDING_BIRTH_CHART } from '../const';

export const readAnonBirthDate = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY_BIRTH_DATE);
        if (!raw) return null;
        // Sanity-check — accept anything Date can parse, return the
        // ISO string format so consumers don't have to re-parse.
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return null;
        return raw;
    } catch {
        return null;
    }
};

export const writeAnonBirthDate = (value) => {
    if (typeof window === 'undefined') return;
    if (!value) return;
    try {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return;
        // Persist as ISO string for stable round-trips.
        window.sessionStorage.setItem(STORAGE_KEY_BIRTH_DATE, date.toISOString());
    } catch {
        // sandboxed iframes / private mode can throw — best-effort write
    }
};

export const clearAnonBirthDate = () => {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(STORAGE_KEY_BIRTH_DATE);
    } catch {
        // ignore
    }
};

/**
 * HORO-10360: anonymous birth chart details, held across the sign-up wall.
 *
 * The anon flow collects date / time / place, then gates on Generate: the
 * details land here, the AuthModal opens, and once auth completes `useAuth`
 * reads them back and generates the chart. Same posture as the birth date
 * above — sessionStorage, cleared on tab close — and same best-effort
 * try/catch, since sandboxed iframes and private mode can throw.
 *
 * Shape: `{ birthDateTime, birthTimeKnown, birthAddress: {placeId, description} }`,
 * i.e. exactly the POST /user/birth-chart body minus `chatId`.
 */
export const writePendingBirthChart = (value) => {
    if (typeof window === 'undefined' || !value) return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY_PENDING_BIRTH_CHART, JSON.stringify(value));
    } catch {
        // best-effort
    }
};

export const readPendingBirthChart = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY_PENDING_BIRTH_CHART);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed.birthDateTime === 'string' ? parsed : null;
    } catch {
        return null;
    }
};

export const clearPendingBirthChart = () => {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(STORAGE_KEY_PENDING_BIRTH_CHART);
    } catch {
        // ignore
    }
};
